/* eslint-disable no-bitwise */
/* eslint-disable @typescript-eslint/naming-convention */
/** @module qt
 *  @description 通用行为
 */
import * as Cookies from 'js-cookie';
import { invokeIosHandler, invokeAndroidHandler, createAppCallback } from '../core';
import { env as envModule } from '../env';
import { invokeSchema, getAppVersion, compareAppVersion } from '../base';
import { getEasyRootDomain } from '../utils';
import config from '../config';

const NODE_ENV = process.env.NODE_ENV as 'production' | 'staging';
const rootDomain = getEasyRootDomain();
const { inApp, inIosApp } = envModule;
const keys = config[NODE_ENV] || config.production;

/**  小米大字号类型枚举, 其中NORMAL为常规字号. */
export enum QtMIUIFontSizeTypeEnum {
  /** 小号字体：12 */
  SMALL = 12,
  /** 正常字号：1 */
  NORMAL = 1,
  /** 中号字体：13 */
  BIG = 13,
  /** 大号字体：14  */
  BIGGER = 14,
  /** 超大字体：15 */
  SUPER_BIG = 15,
  /**  巨无霸字体：11 */
  BIGGEST = 11,
}

/** 购买来源 */
export enum PurchaseSourceTypeEnum {
  /** 专辑 */
  CHANNEL = 'channel',
  /** 小说 */
  NOVEL = 'novel',
}

/** 购买的商品类型来源 */
export enum PurchaseTypeEnum {
  /** 所有 */
  ALL = 'all',
  /** 单集 */
  SINGLE = 'single',
  /** 多集 */
  MULTI = 'multi',
  /** 会员 */
  VIP = 'vip',
}

/** 分享目标 */
export enum ShareTargetEnum {
  /** 微信好友 */
  WX_FRIEND = 'wx_friend',
  /** 微信朋友圈 */
  WX_MOMENT = 'wx_moment',
  /** 微博 */
  WB = 'wb',
  /** QQ 好友 */
  QQ_FRIEND = 'qq_friend',
  /** QQ 朋友圈 */
  QQ_ZONE = 'qq_zone',
}

/** 设备信息 */
export interface DeviceInfo {
  /** 设备 ID */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  deviceId: string;
  /** 应用版本号(8.4.2+支持) */
  appVersion?: string;
  /** 应用版本号(8.4.2+支持) */
  systemVersion?: string;
  /** 应用版本号(8.4.2+支持) */
  systemVersionInt?: number;
  /** 应用版本号(8.9.0+支持) */
  deviceModel?: string;
  /** 渠道(Android 主 App 9.3.4, 小米合作版 第一版开始支持) */
  qtChannel?: string;
  /** 小米 UI 系统字号类型 (Android 主 App 9.3.4, 小米合作版 第一版开始支持) */
  qtMIUIFontSizeType?: QtMIUIFontSizeTypeEnum;
  /** 协议相关，960 开始支持: mac地址 */
  macAddress?: string;
  /** 协议相关，960 开始支持: 运营商(联通、电信、移动) 'unicom' | 'telcom' | 'mobile' | '' */
  carrier?: string;
  /** 协议相关，960 开始支持: 网络状态 '无网络' | '2G' | '3G' | '4G' | '3G' | 'WIFI'| '无法识别' */
  currentNetworkStatus?: string;
  /** 协议相关，960 开始支持: idfa */
  idfa?: string;
  /** 协议相关，960 开始支持: 手机型号 */
  deviceProductName?: string;
}

/** 搜索历史 */
export interface SearchHistory {
  /** 搜索历史列表 */
  SearchHistoryList: string[];
}

let deviceInfo: DeviceInfo | null = null;

/**
 *  登录
 * 登录后可通过getUserId和getAccessToken获取用户信息
 *  @version: 站外, ios, 安卓.
 * @example
 * ```javascript
 * qt.login()
   .then(function(res){
      console.log(res);
   })
   .catch(function(e){
     console.log(e);
   });
 * ```
 *  户端登录成功事件监听
 * > 事件名：`JSSDK_LOGIN`
 * ```javascript
 * window.addEventListener('JSSDK_LOGIN', (e) => {
 *  console.log(e.detail);
 * })
 * ```
 * */
export function login(params: {
  /** 是否只返回实名制用户
   * @defaultValue false
   */
  realNameUserOnly?: boolean;

  /** 重定向链接(仅供站外使用),
   * @defaultValue `location.href` */
  redirectUri?: string;

  /** 发布环境 staging 或 production
   * @defaultValue 'production'
   */
  env?: 'staging' | 'production';

  /** 是否展示使用QQ登录,为 true 时显示 qq 登录方式(仅供站外使用)
   * @defaultValue true */
  qq?: boolean;

  /** 是否展示使用微信登录, 为 true 时显示微信登录方式(仅供站外使用)
   * @defaultValue true  */
  wx?: boolean;

  /** 是否展示使用微登录, 为 true 时显示微博登录方式(仅供站外使用)
   * @defaultValue true  */
  wb?: boolean;
}): Promise<UserInfo> {
  const { realNameUserOnly = false, redirectUri, env = 'production', qq = true, wx = true, wb = true } = params;
  if (inApp) {
    return compareAppVersion('8.5.2')
      .then(({ isGreaterOrEqual }) => {
        // == ios下低于8.5.2使用旧方法
        if (inIosApp && !isGreaterOrEqual) {
          return invokeIosHandler('getUserInfo', {
            showLoginflg: true,
            realNameUserOnly,
          }) as Promise<UserInfo>;
        }
        return invokeSchema('qingtingfm://action.qingting.fm/user/info', {
          show_login_popup: true,
          realNameUserOnly,
        }) as Promise<UserInfo>;
      })
      .then((res) => {
        const loginEvent = new CustomEvent('JSSDK_LOGIN', { detail: res });
        window.dispatchEvent(loginEvent);
        return res;
      });
  }
  const loginUrl = config.login[env];
  const thirdParams = `${qq ? '' : '&qq=0'}${wx ? '' : '&wx=0'}${wb ? '' : '&wb=0'}`;
  const url = `${loginUrl}?${keys?.h5_login_redirect}=${encodeURIComponent(
    redirectUri || window.location.href,
  )}${thirdParams}`;
  window.location.href = url;
  return Promise.resolve({});
}

/** 
 *  登出
 *  @version H5; IOS: 8.4.2+; Android: 8.4.2+ 
 *  @example
 * ```
 * qt.logout()
   .then(function(res){
      console.log(res);
   })
   .catch(function(e){
     console.log(e);
   });
 * ```
 * */
export function logout(): Promise<void> {
  if (inApp) {
    return invokeSchema('qingtingfm://action.qingting.fm/logout') as Promise<void>;
  }
  Cookies.remove(keys.h5_authuser, { domain: `.${rootDomain}` });
  Cookies.remove(keys.h5_user_id, { domain: `.${rootDomain}` });
  Cookies.remove(keys.h5_refresh_token, { domain: `.${rootDomain}` });
  Cookies.remove(keys.h5_access_token, { domain: `.${rootDomain}` });
  return Promise.resolve() as Promise<void>;
}

/** 获取用户id函数的返回值类型 */
export type GetUserIDRes = string | null;

/**  获取用户id，
 * 跳转登录请使用login,通过此接口判断登录态，如果返回如果为null则为未登录
 *  @version 站外; IOS; Android
 *  */
export function getUserId(
  params: {
    /** 是否为真实用户，真实用户则不返回匿名userID
     * @defaultValue  false
     */
    realNameUserOnly?: boolean;
  } = {},
): Promise<GetUserIDRes> {
  const { realNameUserOnly = false } = params;
  if (inApp) {
    return invokeSchema(
      'qingtingfm://action.qingting.fm/user/info',
      {
        show_login_popup: false,
        realNameUserOnly,
      },
      true,
    ).then((res: any = {}) => {
      if (Number(res.code) === 200) {
        const data = res.data || {};
        if (data.is_anonymous && realNameUserOnly) return null;
        return data.user_id || null;
      }
      if (Number(res.code) === 10002) {
        return null;
      }
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({ code: res.code, message: res.message, name: '' } as Error);
    });
  }
  const userId = Cookies.get(keys.h5_user_id) || null;
  return Promise.resolve(userId);
}

/** 获取用户信息的返回值类型 */
export interface UserInfo {
  /** 用户ID */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  userId?: string | null;
  /** 用户名称 */
  userName?: string;
  /** 用户头像 */
  avatar?: string;
  /** 会员信息 */
  vip?: {
    /** 是否是会员 */
    isVip: string;
    /** 会员过期时间  */
    expire: string;
  };
  /** 是否是匿名用户  */
  isAnonymous?: boolean;
}

/**   获取用户信息
 *  @version: 站外, IOS, Android
 *  @example
 * ```javascript
 * qt.getUser()
  .then(function(user){
    console.log(user);
  })
  .catch(function(e){
    console.log(e);
  });
 * ```
 */
export function getUser(
  params: {
    /** 为 true 时可匿名登录
     * @defaultValue false
     */
    realNameUserOnly?: boolean;
  } = {},
): Promise<UserInfo> {
  const { realNameUserOnly = false } = params;
  if (inApp) {
    return invokeSchema('qingtingfm://action.qingting.fm/user/info', {
      show_login_popup: false,
      realNameUserOnly,
    }).then((user: any = {}) => ({
      userId: user.user_id,
      userName: user.user_name,
      avatar: user.avatar,
      vip: {
        isVip: user?.vip?.is_vip,
        expire: user?.vip?.expire,
      },
      isAnonymous: user.is_anonymous,
    }));
  }
  const userId = Cookies.get(keys.h5_user_id) || null;
  return Promise.resolve({ userId });
}

/**  获取token
 *  @version 站外; IOS; Android
 *  @example
 * ````
 * qt.getAccessToken()
  .then(function(accessToken){
    console.log(accessToken);
  })
  .catch(function(e){
    console.log(e);
  });
 * ```` */
export function getAccessToken(): Promise<string | null> {
  if (inApp) {
    return compareAppVersion('8.4.2').then(({ isGreaterOrEqual }) => {
      if (isGreaterOrEqual) {
        return invokeSchema('qingtingfm://action.qingting.fm/getaccesstoken').then((res: any = {}) => res.access_token);
      }
      if (inIosApp) {
        return invokeIosHandler('getUserAccessToken').then((res: any = {}) =>
          Number(res.code) === 1 ? res.accessToken : null,
        );
      }
      return invokeAndroidHandler('QTJsLocal', 'getUserAccessToken').then((res = {}) =>
        Number(res.code) === 1 ? res.accessToken : null,
      );
    });
  }
  const accessToken = Cookies.get(keys.h5_authuser) || null;
  return Promise.resolve(accessToken);
}

/**  获取设备信息
 *  @version 站外(仅返回deviceId); IOS; Android
 *  @example
 * ```javascript
 * qt.getDevice()
  .then(res => {
    console.log(res.deviceId);
  })
  .catch(e => {
    console.log(e.message);
  });
 * ```
 */
export function getDevice(): Promise<DeviceInfo> {
  if (inApp) {
    if (deviceInfo) return Promise.resolve(deviceInfo);
    return invokeSchema('qingtingfm://action.qingting.fm/device/info').then((device: any) => {
      deviceInfo = {
        deviceId: device.device_id,
        appVersion: device.app_version,
        systemVersion: device.system_version,
        systemVersionInt: device.system_version_int,
        deviceModel: device.device_model,
        qtChannel: device.qt_channel,
        qtMIUIFontSizeType: device.qt_miui_font_size || QtMIUIFontSizeTypeEnum.NORMAL,
        macAddress: device.MACAddress,
        carrier: device.carrier,
        currentNetworkStatus: device.currentNetworkStatus,
        idfa: device.idfa,
        deviceProductName: device.deviceProductName,
      };
      return deviceInfo;
    });
  }
  // == 站外deviceId统一使用的是日志库qt-web-monitor的deviceId，后续需统一
  let deviceId: string = localStorage.getItem('weblogDeviceId') || '';
  if (!deviceId) {
    deviceId = `${1e7}-${1e3}-${4e3}-${8e3}-${1e11}`.replace(/[018]/g, (c) =>
      (Number(c) ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(c) / 4)))).toString(16),
    );
    localStorage.setItem('weblogDeviceId', deviceId);
  }
  return Promise.resolve({ deviceId });
}

/**  获取设备信息
 *  @version IOS:9.6.0; Android: 9.6.0;
 *  @example
 * ```javascript
 * qt.getSearchHistory()
  .then(res => {
    console.log(res.SearchHistoryList);
  })
  .catch(e => {
    console.log(e.message);
  });
 * ```
 */
export function getSearchHistory(): Promise<SearchHistory> {
  return invokeSchema('qingtingfm://action.qingting.fm/getSearchHistory') as Promise<SearchHistory>;
}

/**
 *  获取系统信息，
 *  @version IOS:8.6.3; Android: 8.6.3;
 *  @example
 * ```javascript
 * qt.getSystemInfo()
  .then(res => {
    console.log(res);
  })
  .catch(e => {
    console.log(e.message);
  });
 * ```
 */
export function getSystemInfo(): Promise<unknown> {
  return invokeSchema('qingtingfm://action.qingting.fm/shieldSystemInfo');
}

/**
 *  购买
 *  @version 站外, IOS, Android
 * > 注意: 外返回值请在回传后页面的 cookie 中获取，目前只支持微信内浏览器获取
 *  @example
 * ```javascript
 * qt.purchase({ itemId: 'myItemId' })
  .then(res => {
    console.log(res.orderId);
  })
  .catch(e => {
    console.log(e.message);
  });
 * ```
 */
export function purchase(options: {
  /** 购买类型 */
  type?: PurchaseTypeEnum;
  /** 来源类型 */
  sourceType?: PurchaseSourceTypeEnum;
  /** 商品ID */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  itemId: string;
  /** 专辑ID */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  channelId?: string;
  /** 节目 ID */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  programId?: string;
  /** 节目 ID 列表, type=multi 时必填, 多个节目以英文逗号分隔 */
  programIds?: string;
  /** 购买数量, type=all 且商品为赠礼时必填 */
  count?: number;
  /** 产品 ID, IOS 下 IAP 购买时必填 */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  productId?: string;
  /** 分享 ID(仅站外使用) */
  shareId?: string;
  /** 小说 ID(仅站内 sourceType 为 novel 时可用) */
  novelId?: string;
  /** 小说章节 ID(仅站内 sourceType 为 novel 时可用) */
  chapterId?: string;
  /** 顺序，asc、desc(仅站内 sourceType 为 novel 时可用) */
  order?: 'asc' | 'desc';
  /** 合作方(仅站外使用) */
  partner: string;
  /** 购买后跳转回的网址(仅站外使用)
   * @defaultValue `location.href`
   */
  redirectUri: string;
  /** 环境 staging,production(仅站外使用) */
  env: 'production' | 'staging';
}): Promise<{
  /** 订单ID */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  orderId?: string;
}> {
  const {
    type = PurchaseTypeEnum.ALL,
    sourceType,
    itemId,
    channelId,
    programId,
    programIds,
    count,
    productId,
    shareId,
    novelId,
    chapterId,
    order,
    partner,
    redirectUri,
    env = 'production',
  } = options;
  if (inApp) {
    return compareAppVersion('8.4.4').then(({ isLess }) => {
      if (inIosApp && productId) {
        if (isLess) {
          // 低于8.4.4版本ios特殊处理
          return invokeIosHandler('iap', { product_id: productId }).then((res: any) => ({
            orderId: res.trade_id,
          }));
        }
        // ios下8.4.4以上版本使用新的schema
        return invokeSchema('qingtingfm://action.qingting.fm/iap/pay', {
          product_id: productId,
        }).then((res: any) => ({
          orderId: res.order_id,
        }));
      }
      const params: any = { type };
      if (sourceType) params.source_type = sourceType;
      if (itemId) params.item_id = itemId;
      if (channelId) params.channel_id = channelId;
      if (programId) params.program_id = programId;
      if (novelId) params.novel_id = novelId;
      if (chapterId) params.chapter_id = chapterId;
      if (order) params.order = order;
      if (programIds) params.program_ids = programIds;
      if (count) params.count = count;
      return invokeSchema('qingtingfm://action.qingting.fm/user/pay', params, false, true).then((res: any) => ({
        orderId: res.order_id,
      }));
    });
  }
  let url = `${config.purchase[env]}?item_id=${itemId}`;
  if (programId) url += `&program_id=${programId}`;
  if (programIds) url += `&program_ids=${programIds}`;
  if (redirectUri) url += `&redirect_uri=${encodeURIComponent(redirectUri)}`;
  if (shareId) url += `&share_id=${shareId}`;
  if (partner) url += `&partner=${partner}`;
  if (sourceType) url += `&source_type=${sourceType}`;
  if (novelId) url += `&novel_id=${novelId}`;
  if (chapterId) url += `&chapter_id=${chapterId}`;
  if (order) url += `&order=${order}`;
  window.location.href = url;
  return Promise.resolve({});
}

/**
 *  批量购买接口
 * @version H5; iOS: 9.0.2+; android: 9.0.2+
 *  @example
 * ```javascript
 * qt.multiPurchase({ itemId: 'myItemId' })
  .then(res => {
    console.log(res.orderId);
  })
  .catch(e => {
    console.log(e.message);
  });
  * ```
 */
export function multiPurchase(param: {
  /** 来源类型
   * @defaultValue `PurchaseSourceTypeEnum.CHANNEL`
   */
  sourceType?: PurchaseSourceTypeEnum;
  /** 商品ID */
  itemId: string;
  /** 专辑 ID(仅 sourceType 为 channel 时可用) */
  channelId: string;
  /** 节目 ID(仅 sourceType 为 channel 时可用) */
  programId: string;
  /** 小说 ID(仅 sourceType 为 novel 时可用) */
  novelId: string;
  /** 小说章节 ID(仅 sourceType 为 novel 时可用) */
  chapterId: string;
  /** 顺序，asc、desc(仅 sourceType 为 novel 时可用)
   * @defaultValue 'asc'
   */
  order?: 'asc' | 'desc';
}): Promise<{ orderId: string }> {
  const {
    sourceType = PurchaseSourceTypeEnum.CHANNEL,
    itemId,
    channelId,
    programId,
    novelId,
    chapterId,
    order,
  } = param;
  const params: any = { source_type: sourceType };
  if (itemId) params.item_id = itemId;
  if (channelId) params.channel_id = channelId;
  if (programId) params.program_id = programId;
  if (novelId) params.novel_id = novelId;
  if (chapterId) params.chapter_id = chapterId;
  if (order) params.order = order;
  return invokeSchema('qingtingfm://action.qingting.fm/multipurchase', params) as Promise<{ orderId: string }>;
}

/**
 *  分享
 *  @example
 * ```javascript
 * // 普通分享弹窗
qt.share({
  title:'晓年鉴',
  desc:'高晓松：知名音乐人',
  imageUrl:'https://pic.qingting.fm/channel/2018/11/06/c93c2fa20fae653367595142c8779344.jpg',
  link:'https://m.qingting.fm/vchannels/271526/programs/10113149'
}).then(function (res) {
  console.log(res);
}).catch(function (e) {
  console.log(e);
});
// 海报分享弹窗
qt.share({
  poster: true,
  title:'晓年鉴',
  desc:'高晓松：知名音乐人',
  imageUrl:'https://pic.qingting.fm/channel/2018/11/06/c93c2fa20fae653367595142c8779344.jpg',
  link:'https://m.qingting.fm/vchannels/271526/programs/10113149'
});
// 分享微信好友图文信息
qt.share({
  target: 'wx_friend',
  title:'晓年鉴',
  desc:'高晓松：知名音乐人',
  imageUrl:'https://pic.qingting.fm/channel/2018/11/06/c93c2fa20fae653367595142c8779344.jpg',
  link:'https://m.qingting.fm/vchannels/271526/programs/10113149'
});
// 分享微信好友海报
qt.share({
  poster: true,
  target: 'wx_friend',
  title:'晓年鉴',
  desc:'高晓松：知名音乐人',
  imageUrl:'https://pic.qingting.fm/channel/2018/11/06/c93c2fa20fae653367595142c8779344.jpg',
  link:'https://m.qingting.fm/vchannels/271526/programs/10113149'
});
 * ```
 */
export function share(params: {
  /** 分享目标，默认不指定则弹出分享弹窗 */
  target?: ShareTargetEnum;
  /** 是否分享海报  */
  poster?: boolean;
  /** 分享着陆页链接 */
  link?: string;
  /** 分享标题 */
  title: string;
  /** 分享图片 */
  imageUrl?: string;
  /** 分享描述 */
  desc?: string;
}): Promise<void> {
  const { target, poster = false, link, imageUrl, title, desc } = params;
  const shareInfo: any = {};
  if (target) shareInfo.target = target;
  if (poster) {
    shareInfo.type = 'poster';
    shareInfo.page_url = link;
    shareInfo.intro = desc;
    shareInfo.poster = imageUrl;
    shareInfo.direct = true;
  } else {
    shareInfo.type = 'h5';
    shareInfo.h5_link = link;
    shareInfo.h5_title = title;
    shareInfo.h5_desc = desc;
    shareInfo.h5_image = imageUrl;
  }
  return invokeSchema('qingtingfm://action.qingting.fm/share', shareInfo) as Promise<void>;
}

/**
 *  绑定手机号
 * @example
 * ```
 * qt.bindPhone()
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function bindPhone(): Promise<void> {
  return invokeSchema('qingtingfm://action.qingting.fm/account/binding/phone') as Promise<void>;
}

/**
 *  绑定微信号
 *  @example
 * ```javascript
 * qt.bindWechat()
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function bindWechat(params: {
  /** 微信公众号 appId , 默认蜻蜓 fm 官方微信公众号 appId */
  appId?: string;
}): Promise<void> {
  const { appId } = params;
  return invokeSchema('qingtingfm://action.qingting.fm/account/binding/wechat', {
    appId: appId || 'wxe6c1e02d89c62659',
  }) as Promise<void>;
}

/**
 *  唤醒小程序
 * @example
 * ```
 * qt.callMiniProgramme({ path: 'pages/guides/main'})
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function callMiniProgramme(params?: {
  /** 小程序原始 Id	. 默认 蜻蜓 fm 官方小程序原始 Id */
  originId?: string;
  /** 小程序路径 */
  path?: string;
}): Promise<void> {
  const { originId, path = '' } = params || {};
  return invokeSchema('qingtingfm://action.qingting.fm/wx/miniprogram', {
    appId: originId || 'gh_6c99690dfbac',
    path,
  }) as Promise<void>;
}

/**
 *  后退监听
 *  @version IOS: 8.3.3+; android: 8.3.3+
 *  @example
 * ```javascript
 * qt.backPressLinstener({
  onBack: function() {
    // 触发后退
  }
})
 * ```
 */
export function backPressLinstener(params: {
  /** 后退监听回调 */
  onBack?: () => void;
}): Promise<void> {
  const {
    onBack = () => {
      // do nothing
    },
  } = params || {};
  const cb = createAppCallback(onBack, 'backPressLinstener');
  return invokeSchema('qingtingfm://action.qingting.fm/h5OnBackPressInterceptor', {
    back_press_callback: cb.cbName,
  }) as Promise<void>;
}

/**
 *  原生toast
 * @version: ios:8.1.6+; android: 8.4.2+
 * @example
 * ```
 * qt.toast({ text:'toast文案，2秒后消失', duration: 2})
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function toast(params: {
  /** toast 文案 */
  text?: string;
  /** 持续时间(秒)	 */
  duration?: number;
}): Promise<void> {
  const { text = '', duration = 1 } = params;
  return invokeSchema('qingtingfm://action.qingting.fm/displaytoast', {
    text,
    duration,
  }) as Promise<void>;
}

/**
 *  手机是否安装特定包名的app
 *  @version Android 8.4.7+
 *  @example
 * ```javascript
 * qt.hasInstalled({ packageName:'com.ruiqugames.buyuqianpao' })
  .then(function (isInstalled) {
    console.log(isInstalled);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function hasInstalled({ packageName }: { packageName: string }): Promise<boolean> {
  return invokeSchema(`qingtingfm://action.qingting.fm/hasInstalled`, { packageName }, true).then((res: any = {}) => {
    if (Number(res.code) === 200) {
      return true;
    }
    if (Number(res.code) === 500) {
      return false;
    }
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({ code: res.code, message: res.message });
  });
}

/**
 *  跳转到特定包名的app
 * @version: android: 8.4.7+
 * @example
 * ```
 * qt.jumpToPackage({ packageName:'com.ruiqugames.buyuqianpao' });
 * ```
 */
export function jumpToPackage(params: {
  /** 包名 */
  packageName: string;
}): Promise<void> {
  return invokeSchema('qingtingfm://action.qingting.fm/jumpByPackageName', params) as Promise<void>;
}

/**
 * 通知权限确认
 * @version: android:8.6.0+; ios: 8.6.0+
 * @example
 * ```
 * qt.checkNotification({})
  .then(function (isOpened) {
    console.log(isOpened);
  })
 * ```
 */
export function checkNotification(params: {
  /** 是否主动弹出设置页面, 默认true */
  forceRequest?: boolean;
}): Promise<boolean> {
  return invokeSchema('qingtingfm://action.qingting.fm/checkNotificationPermission', params, true).then(
    (res: any = {}) => {
      if (Number(res.code) === 200) {
        return true;
      }
      if (Number(res.code) === 401) {
        return false;
      }
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({ code: res.code, message: res.message, name: 'error' } as Error);
    },
  );
}

/**  是否儿童模式
 * @version: 9.0.8+
 * @example
 * ```
 * qt.getTeenagerMode().then(function (res) {
    console.log(res);
});
 * ```
 */
export function getTeenagerMode(): Promise<{ teenagerMode: boolean }> {
  return invokeSchema('qingtingfm://action.qingting.fm/getTeenagerMode') as Promise<{ teenagerMode: boolean }>;
}

/** 添加日历
 * @example 
 * ```
 * qt.insertCalenderEvent({
        title: '标题信息',
        description: '描述信息',
        start: 1616404073000,
        end: 1616576873000,
        advanceDuration: 300000,
      }).then(function (res) {
        console.log(res);
      }).catch(function (e) {
        console.log(e);
      });
 * ```
 */
export function insertCalenderEvent({
  title,
  description,
  start,
  end,
  advanceDuration,
}: {
  /** 日历事件标题 */
  title: string;
  /** 日历事件描述 */
  description: string;
  /** 事件开始时间戳 */
  start: number;
  /** 事件结束时间戳 */
  end: number;
  /** 提醒时间，距离事件结束时间的毫秒数 */
  advanceDuration: number;
}): Promise<void> {
  return invokeSchema('qingtingfm://action.qingting.fm/insertCalenderEvent', {
    title,
    description,
    start,
    end,
    advanceDuration,
  }) as Promise<void>;
}

/** 拦截客户端touch事件 */
export function preventParentTouchEvent(
  option: {
    intercept?: boolean;
  } = {},
): Promise<void> {
  const { intercept = false } = option;
  return invokeSchema('qingtingfm://action.qingting.fm/preventParentTouchEvent', {
    intercept,
  }) as Promise<void>;
}

/** @ignore */
export const common = {
  login,
  logout,
  getUserId,
  getUser,
  getAccessToken,
  getAppVersion,
  compareAppVersion,
  getDevice,
  getSearchHistory,
  getSystemInfo,
  purchase,
  multiPurchase,
  share,
  bindPhone,
  bindWechat,
  callMiniProgramme,
  backPressLinstener,
  toast,
  hasInstalled,
  jumpToPackage,
  checkNotification,
  getTeenagerMode,
  insertCalenderEvent,
  preventParentTouchEvent,
};
